import { ArrowPathIcon } from "@heroicons/react/24/outline";
import type { PlaylistMeta } from "../../repositories/playlists";

// The line above every playlist table: how many placements, how fresh the data is, and a
// way to fetch newer data.
//
// On stored data (meta.source === "db") it also says what the stored data cannot: that
// nothing has been collected yet, that the last sync was incomplete, that some listed
// placements are not counted, or that the country picked is outside what was stored. On
// live data it is only the count line the tables always had.

interface PlaylistFreshnessProps {
  /** From playlistCountLabel. */
  countLabel: string;
  meta?: PlaylistMeta | null;
  /** Hides the line while the table loads, so it cannot describe the previous tab. */
  loading?: boolean;
  /** Omit to hide the Refresh button. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Why the last refresh did not go through, or what it could not do. */
  refreshMessage?: string | null;
}

/**
 * "Sep 24, 2026, 10:10 AM". Date and time: a crawl from this morning and one from yesterday
 * read the same without the time.
 */
const formatDataAsOf = (value: string): string | null => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** What the stored data cannot show, if anything, as sentences for the user. */
const storedDataNotes = (meta: PlaylistMeta): string[] => {
  const notes: string[] = [];

  if (meta.syncStatus === "never_synced") {
    // A roster artist whose song was pitched already has that song's placements from the
    // pitch sync, before its own first roster sync. Saying nothing was collected would
    // contradict the rows below it.
    notes.push(
      meta.rawTotal > 0
        ? "This hasn't had its own sync yet. Showing placements already collected for its pitched records."
        : "Playlist data hasn't been collected for this yet. It appears after the first sync."
    );
  } else if (meta.syncStatus === "running") {
    notes.push("A sync is in progress. Refresh the page once it finishes for the latest data.");
  } else if (meta.syncStatus === "failed") {
    notes.push("The last sync did not complete. Showing the most recent data collected.");
  }

  if (meta.countryCovered === false) {
    notes.push(
      "Playlist data on this platform is collected for selected markets only, and not for the country selected. What is shown may be incomplete."
    );
  }

  if (meta.staleCount) {
    notes.push(
      `${meta.staleCount} ${meta.staleCount === 1 ? "placement has" : "placements have"} not been confirmed by SoundCharts recently. ${meta.staleCount === 1 ? "It is" : "They are"} listed but not counted.`
    );
  }

  return notes;
};

export const PlaylistFreshness = ({
  countLabel,
  meta,
  loading = false,
  onRefresh,
  refreshing = false,
  refreshMessage,
}: PlaylistFreshnessProps) => {
  if (loading) return null;

  const stored = meta?.source === "db";
  const dataAsOf = stored && meta?.dataAsOf ? formatDataAsOf(meta.dataAsOf) : null;
  const notes = stored && meta ? storedDataNotes(meta) : [];

  return (
    <div className="mb-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-neutral-600">
          {countLabel}
          {dataAsOf && <span className="text-neutral-500"> · Data as of {dataAsOf}</span>}
        </p>

        {/* Refresh writes to stored data, so it means nothing while the page reads live. */}
        {stored && onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            title="Fetch the latest data from SoundCharts. SoundCharts itself updates about once a day."
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors bg-white border rounded-md text-primary-700 border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </button>
        )}
      </div>

      {refreshMessage && <p className="mt-1 text-sm text-amber-700">{refreshMessage}</p>}

      {notes.map((note) => (
        <p key={note} className="mt-1 text-sm text-neutral-500">
          {note}
        </p>
      ))}
    </div>
  );
};

export default PlaylistFreshness;
